import { useCallback, useEffect, useState } from 'react';
import OrganizationService, {
  type OrgMsaRate,
  type UpsertOrgMsaRateBody,
} from '@frontend/AppSettings/services/OrganizationService';
import { useBidCapabilities } from '@frontend/BidSheets/useBidCapabilities';
import { useFinancialVisible } from '@frontend/BidSheets/financialDisplay';
import { useCatalogOptions } from '@frontend/BidSheets/Admin/useCatalogOptions';
import Table from '@frontend/Components/Table/Table';
import type { Column } from '@frontend/Models/Table/TableModels';
import './OrgMsaRatesCard.css';

/**
 * The org-level MSA rate card (P2 Build M) — the LIVE overlay force-account pricing resolves
 * BEFORE the tenant catalog (org rate → catalog default, refuse-if-absent intact; bid rates
 * and the frozen bid snapshot untouched). Engineering environment only — the caller gates on
 * that; this card gates on money.
 *
 * Money boundaries, the MsaRatesPanel pattern exactly: the read is SEE_FINANCIALS on the
 * server (403 for a blind caller), surfaced here as the card simply not rendering — nothing
 * to grey out. Writes are ADMIN_CATALOGS on the server (rate-sheet maintenance is the
 * catalogs' own gate); `isBidAdmin` mirrors that verdict cosmetically. An EMPTY card renders
 * for a financial caller — empty is a true state (catalog pricing), not a failure.
 */
interface OrgMsaRatesCardProps {
  orgId: string;
}

const BASIS_LABEL: Record<OrgMsaRate['basis'], string> = {
  force_account: 'Force account',
  t_and_m: 'T&M',
};

const rate = (v: string | null | undefined): string => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  // Up to 4 fraction digits (the MsaRatesPanel rule): rates live at NUMERIC(14,4), and
  // rounding a commercial term to cents would display a rate the MSA never agreed.
  return Number.isFinite(n)
    ? n.toLocaleString('en-US', {
        style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4,
      })
    : '—';
};

const EMPTY_FORM = {
  kind: 'labour' as 'labour' | 'equipment',
  catalogId: '',
  basis: 'force_account' as 'force_account' | 't_and_m',
  rate: '',
};

interface OrgMsaRateFormProps {
  orgId: string;
  onSaved: () => Promise<void>;
  onError: (message: string | null) => void;
}

/** The admin's cell editor — its OWN component so the catalog-options fetches happen only
 * when an ADMIN_CATALOGS holder actually gets a form (review L3: hooks at the card's top
 * level fired two catalog GETs for blind and read-only callers before the early return). */
const OrgMsaRateForm = ({ orgId, onSaved, onError }: OrgMsaRateFormProps): JSX.Element => {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const labourOptions = useCatalogOptions('labour');
  const equipmentOptions = useCatalogOptions('equipment');
  const options = form.kind === 'labour' ? labourOptions : equipmentOptions;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.catalogId || form.rate === '') return;
    const body: UpsertOrgMsaRateBody = {
      kind: form.kind,
      basis: form.basis,
      rate: Number(form.rate),
      ...(form.kind === 'labour'
        ? { labour_role_id: form.catalogId }
        : { equipment_item_id: form.catalogId }),
    };
    setSaving(true);
    try {
      await OrganizationService.upsertMsaRate(orgId, body);
      setForm({ ...EMPTY_FORM });
      onError(null);
      await onSaved();
    } catch (err: any) {
      // The server's refusal verbatim (retired item, deactivated org) — a silent reset
      // teaches the admin the rate saved.
      onError(err?.message ?? 'Could not save the rate.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="org-msa-form" onSubmit={(e) => { void submit(e); }}>
      <label>
        Kind
        <select
          value={form.kind}
          onChange={(e) => setForm({
            ...form, kind: e.target.value as typeof form.kind, catalogId: '' })}
        >
          <option value="labour">Labour</option>
          <option value="equipment">Equipment</option>
        </select>
      </label>
      <label>
        {form.kind === 'labour' ? 'Role' : 'Equipment'}
        <select
          value={form.catalogId}
          onChange={(e) => setForm({ ...form, catalogId: e.target.value })}
        >
          <option value="">Pick from catalog…</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>
      </label>
      <label>
        Basis
        <select
          value={form.basis}
          onChange={(e) => setForm({ ...form, basis: e.target.value as typeof form.basis })}
        >
          <option value="force_account">Force account</option>
          <option value="t_and_m">T&amp;M</option>
        </select>
      </label>
      <label>
        Rate
        <input
          type="number" min="0" step="0.0001" value={form.rate}
          onChange={(e) => setForm({ ...form, rate: e.target.value })}
        />
      </label>
      <button type="submit" disabled={saving || !form.catalogId || form.rate === ''}>
        {saving ? 'Saving…' : 'Save rate'}
      </button>
    </form>
  );
};

const OrgMsaRatesCard = ({ orgId }: OrgMsaRatesCardProps): JSX.Element | null => {
  const financialVisible = useFinancialVisible();
  const capabilities = useBidCapabilities();
  const mayAdmin = capabilities?.isBidAdmin ?? false;
  const [rates, setRates] = useState<OrgMsaRate[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setRates(await OrganizationService.getMsaRates(orgId));
    } catch {
      // A 403/404 (visibility raced, org gone) renders NO card — not a lying empty one.
      setRates(null);
    }
  }, [orgId]);

  useEffect(() => {
    // Reset FIRST (the MsaRatesPanel lesson): a card kept while a new orgId resolves to
    // 403/404 would render org A's rates against org B.
    setRates(null);
    if (!financialVisible) return;
    void reload();
  }, [orgId, financialVisible, reload]);

  if (!financialVisible || rates === null) return null;

  const remove = async (rateId: string) => {
    try {
      await OrganizationService.deleteMsaRate(orgId, rateId);
      setError(null);
      await reload();
    } catch (err: any) {
      setError(err?.message ?? 'Could not remove the rate.');
    }
  };

  const columns: Column<OrgMsaRate>[] = [
    { header: 'Kind', render: (r) => (r.kind === 'labour' ? 'Labour' : 'Equipment') },
    { header: 'Item', render: (r) => r.name ?? '—' },
    { header: 'Basis', render: (r) => BASIS_LABEL[r.basis] },
    { header: 'Rate', render: (r) => rate(r.rate) },
    ...(mayAdmin
      ? [{
          header: '',
          render: (r: OrgMsaRate) => (
            <button
              type="button"
              className="org-msa-remove-btn"
              onClick={() => { void remove(r.id); }}
            >
              Remove
            </button>
          ),
        }]
      : []),
  ];

  return (
    <section className="stakeholder-section-block org-msa-rates-card">
      <h4>MSA rate card</h4>
      <p className="org-msa-subtitle">
        Force-account pricing resolves this organization&apos;s card before the tenant catalog —
        an empty card means catalog pricing, and every priced line names its source. Rates are
        live (a matter needing frozen rates has its bid snapshot); T&amp;M rows are stored for
        the customer&apos;s MSA and price nothing yet.
      </p>
      <Table
        columns={columns}
        data={rates}
        rowKey="id"
        emptyMessage="No organization rates on file — force-account work prices from the catalog."
      />
      {mayAdmin && <OrgMsaRateForm orgId={orgId} onSaved={reload} onError={setError} />}
      {error && <p className="org-msa-error">{error}</p>}
    </section>
  );
};

export default OrgMsaRatesCard;
